import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../config/api';
import { FiLink, FiCopy, FiCheck, FiAlertTriangle, FiTrash2, FiDollarSign, FiExternalLink, FiMail, FiStar, FiDownload, FiRefreshCw } from 'react-icons/fi';
import B2BAgentsPanel from '../components/B2BAgentsPanel';
import { FaWhatsapp } from 'react-icons/fa';
import SyncHealthWidget from '../components/SyncHealthWidget';
import QuoteModal from '../components/quote/QuoteModal';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calLinks, setCalLinks] = useState([]); // list of all active links
  const [showNewForm, setShowNewForm] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [linkName, setLinkName] = useState('');
  const [linkPhone, setLinkPhone] = useState('');
  const [linkRole, setLinkRole] = useState('editor');
  const [showQuote, setShowQuote] = useState(false);
  const [indexNowBusy, setIndexNowBusy] = useState(false);
  const [indexNowMsg, setIndexNowMsg] = useState('');
  const pingIndexNow = async () => {
    setIndexNowBusy(true);
    try {
      const { data } = await api.post('/admin/seo/indexnow');
      setIndexNowMsg(`Submitted ${data.submitted} pages · status ${data.status_code}`);
    } catch (e) {
      setIndexNowMsg(e?.response?.data?.detail || 'Ping failed');
    } finally { setIndexNowBusy(false); }
  };

  // Payment-link recovery tool
  const [payLookup, setPayLookup] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('Balance payment');
  const [payBookings, setPayBookings] = useState([]);
  const [payTargetId, setPayTargetId] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [payError, setPayError] = useState(null);
  const [payCopied, setPayCopied] = useState(false);

  // Booking-totals fix tool (for legacy/incorrect bookings)
  const [fixGrandTotal, setFixGrandTotal] = useState('');
  const [fixAmountPaid, setFixAmountPaid] = useState('');
  const [fixBusy, setFixBusy] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const [fixError, setFixError] = useState(null);

  // Balance reminder email (manual trigger)
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderMsg, setReminderMsg] = useState(null);

  // Guest feedback (post-stay)
  const [feedback, setFeedback] = useState([]);
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  const loadFeedback = async () => {
    setFeedbackBusy(true);
    try {
      const res = await api.get('/admin/guest-feedback');
      setFeedback(res.data || []);
    } catch {
      setFeedback([]);
    } finally {
      setFeedbackBusy(false);
    }
  };

  const exportFeedbackCSV = () => {
    const header = ['Date', 'Booking ID', 'Guest', 'Email', 'Phone', 'Villa', 'Check-out', 'Rating', 'Recommend', 'Loved', 'Improve'];
    const rows = feedback.map(f => [
      f.created_at, f.booking_id, f.guest_name, f.guest_email, f.guest_phone,
      f.villa_name, f.check_out, f.rating, f.recommend ? 'Yes' : 'No',
      (f.loved || '').replace(/[\r\n,]+/g, ' '),
      (f.improve || '').replace(/[\r\n,]+/g, ' '),
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `guest-feedback-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const deleteFeedback = async (fid) => {
    if (!window.confirm('Delete this feedback entry?')) return;
    try {
      await api.delete(`/admin/guest-feedback/${fid}`);
      setFeedback(prev => prev.filter(f => f.id !== fid));
    } catch (e) {
      alert('Failed to delete');
    }
  };

  // ====== Inline Monthly Report (Jan/Feb/Mar... tabs) ======
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // ====== Coupons (admin-generated, single-use, locked to guest) ======
  const [coupons, setCoupons] = useState([]);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState('');
  const [couponEmail, setCouponEmail] = useState('');
  const [couponPhone, setCouponPhone] = useState('');
  const [couponNotes, setCouponNotes] = useState('');
  const [couponCustomCode, setCouponCustomCode] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMsg, setCouponMsg] = useState(null);
  const [couponCopied, setCouponCopied] = useState(null);

  const fetchCoupons = () => {
    api.get('/admin/coupons')
      .then(res => setCoupons(res.data.coupons || []))
      .catch(() => setCoupons([]));
  };

  const handleCreateCoupon = async () => {
    setCouponMsg(null);
    const amt = parseInt(couponDiscount, 10);
    if (!amt || amt <= 0) { setCouponMsg({ type: 'error', text: 'Enter a valid discount amount in Rs' }); return; }
    if (!couponEmail.trim() && !couponPhone.trim()) {
      setCouponMsg({ type: 'error', text: 'Lock the coupon to at least an email or phone (so only that guest can redeem)' });
      return;
    }
    setCouponBusy(true);
    try {
      const res = await api.post('/admin/coupons', {
        discount_amount: amt,
        guest_email: couponEmail.trim() || null,
        guest_phone: couponPhone.trim() || null,
        notes: couponNotes.trim() || null,
        code: couponCustomCode.trim() || null,
      });
      setCouponMsg({ type: 'success', text: `Coupon ${res.data.code} created — Rs.${amt.toLocaleString()} off` });
      setCouponDiscount(''); setCouponEmail(''); setCouponPhone(''); setCouponNotes(''); setCouponCustomCode('');
      setShowCouponForm(false);
      fetchCoupons();
    } catch (err) {
      setCouponMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to create coupon' });
    }
    setCouponBusy(false);
  };

  const handleRevokeCoupon = async (code) => {
    if (!window.confirm(`Revoke coupon ${code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/coupons/${code}`);
      fetchCoupons();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke coupon');
    }
  };

  const handleCopyCoupon = (code) => {
    navigator.clipboard.writeText(code);
    setCouponCopied(code);
    setTimeout(() => setCouponCopied(null), 2000);
  };

  // Send the coupon to the locked guest's email via Resend
  const [couponEmailBusy, setCouponEmailBusy] = useState({});
  const handleEmailCoupon = async (c) => {
    if (!c.guest_email) {
      alert('This coupon is not locked to an email — cannot send.');
      return;
    }
    if (!window.confirm(`Email coupon ${c.code} to ${c.guest_email}?`)) return;
    setCouponEmailBusy(prev => ({ ...prev, [c.code]: true }));
    try {
      await api.post(`/admin/coupons/${c.code}/send-email`, {});
      setCouponMsg({ type: 'success', text: `Coupon ${c.code} emailed to ${c.guest_email}` });
      fetchCoupons();
    } catch (err) {
      setCouponMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to send email' });
    }
    setCouponEmailBusy(prev => ({ ...prev, [c.code]: false }));
  };

  // Open WhatsApp prefilled with the coupon message to the locked phone
  const handleWhatsAppCoupon = (c) => {
    if (!c.guest_phone) {
      alert('This coupon is not locked to a phone number — cannot send via WhatsApp.');
      return;
    }
    const siteUrl = `${window.location.origin}/booking`;
    const lines = [
      '🎟️ *Royal Garden Villas — Personal Coupon*',
      '',
      `Hi! As discussed, here's your personal Rs.${c.discount_amount.toLocaleString()} discount coupon:`,
      '',
      `*Coupon Code:* ${c.code}`,
      `*You Save:* Rs.${c.discount_amount.toLocaleString()}`,
      '',
      '*How to redeem:*',
      `1. Visit ${siteUrl}`,
      '2. Pick your dates & villa',
      '3. On the price summary, enter the code above in "Have a coupon code?"',
      '4. Tap Apply — discount deducted instantly',
      '',
      '_This coupon is single-use and locked to your contact details. Expires after first use._',
      '',
      '— Royal Garden Villas, Lonavala',
    ];
    const msg = lines.join('\n');
    // c.guest_phone is normalised to last 10 digits — prepend +91 for wa.me
    const phone = String(c.guest_phone).replace(/\D/g, '').slice(-10);
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const navigate = useNavigate();

  const fetchLinks = () => {
    api.get('/admin/calendar-link/list')
      .then(res => setCalLinks(res.data.links || []))
      .catch(() => setCalLinks([]));
  };

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const parsed = JSON.parse(stored);
    if (!parsed.is_admin) { navigate('/dashboard'); return; }
    setUser(parsed);

    api.get('/admin/analytics')
      .then(res => setAnalytics(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    fetchLinks();
    fetchCoupons();
    loadFeedback();
  }, [navigate]);

  // Re-fetch monthly report whenever month/year changes
  useEffect(() => {
    let ignore = false;
    const run = async () => {
      setReportLoading(true);
      try {
        const res = await api.get(`/admin/monthly-report?month=${reportMonth}&year=${reportYear}`);
        if (!ignore) setReport(res.data);
      } catch (err) {
        if (!ignore) setReport(null);
      }
      if (!ignore) setReportLoading(false);
    };
    run();
    return () => { ignore = true; };
  }, [reportMonth, reportYear]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleGenerate = async () => {
    if (!linkName.trim()) return;
    await api.post('/admin/calendar-link/generate', { name: linkName, phone: linkPhone, role: linkRole });
    setLinkName(''); setLinkPhone(''); setLinkRole('editor'); setShowNewForm(false);
    fetchLinks();
  };

  const handleCopy = (token) => {
    const url = `${window.location.origin}/admin-cal/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleDeleteLink = async (token) => {
    if (!window.confirm('Delete this calendar link? The recipient will lose access immediately.')) return;
    try {
      await api.delete(`/admin/calendar-link/${token}`);
      fetchLinks();
    } catch { /* noop */ }
  };

  const handleRemoveDevice = async (token, idx) => {
    if (!window.confirm('Remove this device access?')) return;
    try {
      await api.delete(`/admin/calendar-link/${token}/remove-device/${idx}`);
      fetchLinks();
    } catch { /* noop */ }
  };

  const handlePayLookup = async () => {
    setPayError(null); setPayResult(null); setPayBookings([]); setPayTargetId('');
    if (!payLookup.trim()) return;
    try {
      const res = await api.get(`/admin/bookings/lookup/${encodeURIComponent(payLookup.trim())}`);
      setPayBookings(res.data.bookings || []);
      if ((res.data.bookings || []).length === 1) setPayTargetId(res.data.bookings[0].id);
      if ((res.data.bookings || []).length === 0) setPayError('No booking found for that reference.');
    } catch (err) {
      setPayError(err.response?.data?.detail || 'Lookup failed');
    }
  };

  const handleCreatePayLink = async () => {
    setPayError(null); setPayResult(null);
    const amt = parseInt(payAmount, 10);
    if (!payTargetId) { setPayError('Select a booking first'); return; }
    if (!amt || amt <= 0) { setPayError('Enter a valid amount in INR'); return; }
    setPayBusy(true);
    try {
      const res = await api.post(`/admin/bookings/${payTargetId}/payment-link`, {
        amount_inr: amt,
        note: payNote || 'Balance payment',
      });
      setPayResult(res.data);
    } catch (err) {
      setPayError(err.response?.data?.detail || 'Failed to create payment link');
    }
    setPayBusy(false);
  };

  const handleCopyPayLink = () => {
    if (!payResult?.short_url) return;
    navigator.clipboard.writeText(payResult.short_url);
    setPayCopied(true);
    setTimeout(() => setPayCopied(false), 2000);
  };

  const handleFixTotals = async () => {
    setFixError(null); setFixResult(null);
    if (!payTargetId) { setFixError('Select a booking first'); return; }
    const gt = parseInt(fixGrandTotal, 10);
    const ap = parseInt(fixAmountPaid, 10);
    if (!gt || gt <= 0) { setFixError('Enter a valid grand total'); return; }
    setFixBusy(true);
    try {
      const payload = { grand_total: gt };
      if (!isNaN(ap)) payload.amount_paid = ap;
      const res = await api.post(`/admin/bookings/${payTargetId}/update-totals`, payload);
      setFixResult(res.data);
      // refresh the bookings list so the new outstanding shows
      handlePayLookup();
    } catch (err) {
      setFixError(err.response?.data?.detail || 'Failed to update booking totals');
    }
    setFixBusy(false);
  };

  const handleSendReminder = async () => {
    setReminderMsg(null);
    if (!payTargetId) { setReminderMsg({ type: 'error', text: 'Select a booking first' }); return; }
    setReminderBusy(true);
    try {
      const res = await api.post(`/admin/bookings/${payTargetId}/send-balance-reminder`);
      setReminderMsg({ type: 'success', text: `Reminder email sent to ${res.data.to} for Rs.${res.data.outstanding.toLocaleString()}` });
    } catch (err) {
      setReminderMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to send reminder' });
    }
    setReminderBusy(false);
  };

  if (!user) return null;

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen bg-[#F7F8FA]">
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-start mb-12">
            <div>
              <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Admin Panel</p>
              <h1 className="text-3xl md:text-4xl font-light text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Admin <span className="text-[#F57C20]">Dashboard</span>
              </h1>
            </div>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="border border-[#F57C20] text-[#F57C20] px-6 py-2 text-xs tracking-[0.2em] uppercase rounded-lg hover:bg-[#F57C20] hover:text-white transition-all">
              Logout
            </button>
          </div>

          {/* Analytics Cards */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[1,2,3,4].map(i => <div key={i} className="bg-gray-200 animate-pulse h-28 rounded-xl" />)}
            </div>
          ) : analytics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-[#1B2432] rounded-xl p-6">
                <p className="text-white/50 text-xs tracking-wider uppercase mb-2">Total Bookings</p>
                <p className="text-2xl text-white font-light" data-testid="total-bookings">{analytics.total_bookings}</p>
              </div>
              <div className="bg-[#1B2432] rounded-xl p-6">
                <p className="text-white/50 text-xs tracking-wider uppercase mb-2">Confirmed</p>
                <p className="text-2xl text-[#F57C20] font-light" data-testid="confirmed-bookings">{analytics.confirmed_bookings}</p>
              </div>
              <div className="bg-[#1B2432] rounded-xl p-6">
                <p className="text-white/50 text-xs tracking-wider uppercase mb-2">Revenue (Website)</p>
                <p className="text-2xl text-white font-light" data-testid="total-revenue">Rs.{analytics.total_revenue?.toLocaleString()}</p>
                <p className="text-white/30 text-[10px] mt-1">{analytics.website_paid_bookings || 0} paid online</p>
              </div>
              <div className="bg-[#1B2432] rounded-xl p-6">
                <p className="text-white/50 text-xs tracking-wider uppercase mb-2">Pending</p>
                <p className="text-2xl text-white font-light" data-testid="pending-bookings">{analytics.pending_bookings}</p>
              </div>
            </div>
          )}

          {/* Airbnb Sync Health */}
          <div className="mb-12">
            <SyncHealthWidget
              fetchHealth={() => api.get('/ical/health')}
              triggerSync={(vid) => api.post(`/ical/sync/${vid}`)}
              copyUrlBaseOverride="https://royalgardenvillas.in"
            />
          </div>

          {/* Guest Feedback App Link Generator */}
          <div className="mb-12 bg-[#1B2432] rounded-xl p-5" data-testid="feedback-app-card">
            <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">RGV — Guest Feedback App</p>
            <p className="text-white text-sm mb-3">Generate an app link tied to a specific mobile (Staff device or Host device). Only that mobile number can unlock the link.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input id="fb-phone" placeholder="Mobile (10 digits)" data-testid="fb-phone"
                className="bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-white/10" />
              <select id="fb-role" defaultValue="staff" data-testid="fb-role"
                className="bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-white/10">
                <option value="staff">Staff (submit ratings + upload photos)</option>
                <option value="host">Host (view-only, private notes, photos)</option>
              </select>
              <button data-testid="fb-generate" onClick={async () => {
                const phone = document.getElementById('fb-phone').value;
                const role = document.getElementById('fb-role').value;
                try {
                  const res = await api.post('/admin/feedback-app/tokens', { phone, role });
                  const url = `${window.location.origin}/rgv-feedback/${res.data.token}`;
                  await navigator.clipboard.writeText(url).catch(() => {});
                  alert(`Link copied — share via WhatsApp:\n${url}`);
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed');
                }
              }} className="bg-[#F57C20] hover:bg-[#e56d10] text-white px-4 py-2 text-sm font-semibold rounded">
                Generate &amp; Copy Link
              </button>
            </div>
            <p className="text-[#9CA3AF] text-[11px] mt-2">Cap: 300★/month = Rs.3,000 variable pay + Rs.15,000 base. Link is installable as PWA and locked to the given mobile.</p>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/admin/monthly-report" className="bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-[#F57C20]/30 transition-all group" data-testid="monthly-report-link">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-[#F57C20] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>Monthly Report</h3>
              <p className="text-white/50 text-sm font-light">View booking reports by month</p>
            </Link>
            <div className="bg-[#1B2432] rounded-xl p-8" data-testid="admin-cal-link-card">
              <h3 className="text-lg font-light text-white mb-2 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                <FiLink className="w-4 h-4 text-[#F57C20]" /> Admin Calendar App
              </h3>
              <p className="text-white/50 text-sm font-light mb-4">Shareable links — each person gets their own. Install as app on mobile. Block/unblock dates.</p>

              {/* Active Links */}
              {calLinks.length > 0 && (
                <div className="space-y-4 mb-4">
                  {calLinks.map((link) => (
                    <div key={link.token} className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid={`cal-link-${link.token.slice(0, 8)}`}>
                      {(link.recipient_name || link.recipient_phone) && (
                        <div className="bg-[#F57C20]/10 rounded px-2.5 py-1.5 mb-2 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[#F57C20] text-xs font-semibold">{link.recipient_name || 'Unnamed'}</p>
                            {link.recipient_phone && <p className="text-white/50 text-[10px]">{link.recipient_phone}</p>}
                          </div>
                          <span
                            data-testid={`role-badge-${link.token.slice(0, 8)}`}
                            className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                              link.role === 'viewer'
                                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                                : 'bg-[#F57C20]/20 text-[#F57C20] border border-[#F57C20]/30'
                            }`}
                          >
                            {link.role === 'viewer' ? '👁 Viewer' : '✎ Editor'}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 bg-black/20 rounded px-2.5 py-1.5 mb-2">
                        <input
                          readOnly
                          value={`${window.location.origin}/admin-cal/${link.token}`}
                          className="flex-1 bg-transparent text-white/80 text-[11px] font-mono outline-none min-w-0"
                          data-testid={`cal-link-url-${link.token.slice(0, 8)}`}
                        />
                        <button
                          onClick={() => handleCopy(link.token)}
                          className="text-[#F57C20] hover:text-white transition-colors flex-shrink-0"
                          data-testid={`cal-link-copy-${link.token.slice(0, 8)}`}
                          title="Copy link"
                        >
                          {copiedToken === link.token ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/30 text-[10px]">{link.device_count}/{link.max_devices} devices</span>
                        <button
                          onClick={() => handleDeleteLink(link.token)}
                          className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                          data-testid={`cal-link-delete-${link.token.slice(0, 8)}`}
                          title="Delete this link"
                        >
                          <FiTrash2 className="w-3 h-3" /> Delete link
                        </button>
                      </div>
                      {link.devices && link.devices.length > 0 && (
                        <div className="space-y-1 pt-2 border-t border-white/10">
                          {link.devices.map((device, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-black/10 rounded px-2 py-1">
                              <div className="flex-1 min-w-0">
                                <p className="text-white/60 text-[10px] truncate">{typeof device === 'object' ? device.info : device}</p>
                                {typeof device === 'object' && device.registered_at && (
                                  <p className="text-white/25 text-[9px]">{new Date(device.registered_at).toLocaleDateString()}</p>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveDevice(link.token, idx)}
                                className="text-red-400/70 hover:text-red-300 ml-2 p-0.5"
                                title="Revoke this device"
                              >
                                <FiTrash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {calLinks.length === 0 && !showNewForm && (
                <p className="text-white/30 text-xs italic mb-3">No active calendar links yet.</p>
              )}

              {/* New Link Form */}
              {showNewForm ? (
                <div className="space-y-3 bg-white/5 rounded-lg p-3" data-testid="new-cal-link-form">
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Recipient Name</label>
                    <input value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="e.g. Caretaker Raju" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20]" />
                  </div>
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Phone Number (optional)</label>
                    <input value={linkPhone} onChange={e => setLinkPhone(e.target.value)} placeholder="e.g. 9820088292" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20]" />
                  </div>
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Access Level</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        data-testid="role-editor-btn"
                        onClick={() => setLinkRole('editor')}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                          linkRole === 'editor'
                            ? 'bg-[#F57C20] text-white border-[#F57C20]'
                            : 'bg-white/5 text-white/70 border-white/10 hover:border-white/30'
                        }`}
                      >
                        Editor · Full Access
                      </button>
                      <button
                        type="button"
                        data-testid="role-viewer-btn"
                        onClick={() => setLinkRole('viewer')}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                          linkRole === 'viewer'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white/5 text-white/70 border-white/10 hover:border-white/30'
                        }`}
                      >
                        Viewer · Read-Only
                      </button>
                    </div>
                    <p className="text-white/40 text-[10px] mt-1.5 italic">
                      {linkRole === 'editor'
                        ? 'Can view, block dates, and remove blocks.'
                        : 'Can only view bookings & calendar. Cannot block or remove dates.'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid="generate-cal-link-btn"
                      disabled={!linkName.trim()}
                      onClick={handleGenerate}
                      className="bg-[#F57C20] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#e06b15] transition-colors flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Create Link
                    </button>
                    <button
                      onClick={() => { setShowNewForm(false); setLinkName(''); setLinkPhone(''); }}
                      className="bg-white/5 text-white/70 hover:text-white border border-white/10 px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="show-new-cal-link-form-btn"
                  onClick={() => setShowNewForm(true)}
                  className="bg-[#F57C20] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#e06b15] transition-colors w-full"
                >
                  + Generate New Link
                </button>
              )}
            </div>
            <button type="button" onClick={pingIndexNow} disabled={indexNowBusy} className="text-left bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-[#F57C20]/30 transition-all group disabled:opacity-60" data-testid="admin-indexnow-tile">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-[#F57C20] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{indexNowBusy ? 'Notifying…' : 'Notify Search Engines'}</h3>
              <p data-testid="admin-indexnow-status" className="text-white/50 text-sm font-light">{indexNowMsg || 'Instant indexing ping to Bing, ChatGPT search & Copilot (IndexNow)'}</p>
            </button>
            <button type="button" onClick={() => setShowQuote(true)} className="text-left bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-[#F57C20]/30 transition-all group border border-[#F57C20]/30" data-testid="admin-quotation-tile">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-[#F57C20] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>Generate Quotation</h3>
              <p className="text-white/50 text-sm font-light">PDF quotes · WhatsApp &amp; Email · guest leads</p>
            </button>
            <Link to="/admin/ical-sync" className="bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-[#F57C20]/30 transition-all group" data-testid="ical-sync-link">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-[#F57C20] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>iCal Sync</h3>
              <p className="text-white/50 text-sm font-light">2-way Airbnb calendar sync</p>
            </Link>
            <Link to="/admin/rate-management" className="bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-[#F57C20]/30 transition-all group" data-testid="rate-management-link">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-[#F57C20] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>Rate Management</h3>
              <p className="text-white/50 text-sm font-light">Per-villa & per-date pricing</p>
            </Link>
            <Link to="/admin/published-reviews" className="bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-[#F57C20]/30 transition-all group" data-testid="published-reviews-link">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-[#F57C20] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>Published Reviews</h3>
              <p className="text-white/50 text-sm font-light">Moderate the Home page wall</p>
            </Link>
            <Link to="/admin/danger-zone" className="bg-[#1B2432] rounded-xl p-8 hover:ring-1 hover:ring-red-500/40 transition-all group border border-red-500/30" data-testid="danger-zone-link">
              <h3 className="text-lg font-light text-white mb-2 group-hover:text-red-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>Danger Zone</h3>
              <p className="text-white/50 text-sm font-light">Combo villa + cooking amenity kill switches</p>
            </Link>
          </div>

          {/* Inline Monthly Report — tabs for quick at-a-glance revenue review */}
          <div className="mt-8 bg-[#1B2432] rounded-xl p-6 md:p-8" data-testid="inline-monthly-report">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl font-light text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Monthly <span className="text-[#F57C20]">Report</span>
                </h2>
                <p className="text-white/40 text-[11px]">Website-paid bookings only · Razorpay-completed</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={reportYear}
                  onChange={e => setReportYear(parseInt(e.target.value, 10))}
                  data-testid="inline-report-year"
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#F57C20]"
                >
                  {[2024, 2025, 2026, 2027].map(y => (
                    <option key={y} value={y} className="bg-[#131722]">{y}</option>
                  ))}
                </select>
                <Link
                  to="/admin/monthly-report"
                  className="text-[10px] text-[#F57C20] hover:text-white underline whitespace-nowrap"
                  data-testid="open-full-monthly-report"
                >
                  Open full report →
                </Link>
              </div>
            </div>

            {/* Month tabs */}
            <div className="flex flex-wrap gap-1.5 mb-5" data-testid="month-tabs">
              {MONTHS_SHORT.map((m, i) => {
                const monthNum = i + 1;
                const active = reportMonth === monthNum;
                return (
                  <button
                    key={m}
                    type="button"
                    data-testid={`month-tab-${monthNum}`}
                    onClick={() => setReportMonth(monthNum)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      active
                        ? 'bg-[#F57C20] text-white shadow-sm'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>

            {/* Summary stats */}
            {reportLoading ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-lg h-16 animate-pulse" />
                <div className="bg-white/5 rounded-lg h-16 animate-pulse" />
              </div>
            ) : report ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white/5 rounded-lg p-3" data-testid="report-bookings-count">
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">Bookings</p>
                    <p className="text-white text-xl font-bold">{report.total_bookings || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#F57C20]/15 to-transparent border border-[#F57C20]/20 rounded-lg p-3" data-testid="report-revenue-amount">
                    <p className="text-[#F57C20] text-[10px] uppercase tracking-wider mb-0.5 font-semibold">Revenue</p>
                    <p className="text-white text-xl font-bold">Rs.{(report.total_revenue || 0).toLocaleString()}</p>
                  </div>
                </div>
                {report.entries?.length > 0 ? (
                  <div className="space-y-1.5">
                    {report.entries.slice(0, 5).map((e, idx) => (
                      <div key={idx} className="bg-white/5 rounded-md px-3 py-2 flex items-center justify-between gap-2 text-xs">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium truncate">{e.guest_name || '—'}</p>
                          <p className="text-white/40 text-[10px] truncate">
                            {e.villa_name} · {e.check_in} · {e.nights}n
                          </p>
                        </div>
                        <span className="text-[#F57C20] font-semibold whitespace-nowrap">
                          Rs.{(e.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {report.entries.length > 5 && (
                      <p className="text-white/30 text-[10px] italic text-center pt-1">
                        + {report.entries.length - 5} more · open full report for details
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-white/30 text-xs italic text-center py-4">
                    No website-paid bookings for {MONTHS_SHORT[reportMonth - 1]} {reportYear}.
                  </p>
                )}
              </>
            ) : (
              <p className="text-white/30 text-xs italic">Could not load report.</p>
            )}
          </div>

          {/* Coupons: single-use discount codes */}
          <div className="mt-8 bg-[#1B2432] rounded-xl p-8" data-testid="coupons-section">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl font-light text-white mb-1 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  🎟️ Coupons
                </h2>
                <p className="text-white/50 text-xs">
                  Single-use discount codes for guests negotiating an additional discount. Locked to a specific guest (email and/or phone) — expires after the first booking.
                </p>
              </div>
              {!showCouponForm && (
                <button
                  data-testid="show-new-coupon-form-btn"
                  onClick={() => { setShowCouponForm(true); setCouponMsg(null); }}
                  className="bg-[#F57C20] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#e06b15] transition-colors whitespace-nowrap"
                >
                  + Create Coupon
                </button>
              )}
            </div>

            {couponMsg && (
              <div className={`mb-4 px-3 py-2 rounded-lg text-xs ${couponMsg.type === 'error' ? 'bg-red-500/10 text-red-300 border border-red-500/30' : 'bg-green-500/10 text-green-300 border border-green-500/30'}`}>
                {couponMsg.text}
              </div>
            )}

            {showCouponForm && (
              <div className="bg-white/5 rounded-lg p-4 mb-4" data-testid="new-coupon-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Discount Amount (Rs.) *</label>
                    <input
                      type="number"
                      value={couponDiscount}
                      onChange={e => setCouponDiscount(e.target.value)}
                      placeholder="e.g. 2000"
                      data-testid="coupon-discount-input"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20]"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Custom Code (optional)</label>
                    <input
                      value={couponCustomCode}
                      onChange={e => setCouponCustomCode(e.target.value.toUpperCase())}
                      placeholder="Leave blank for auto-generate"
                      data-testid="coupon-custom-code-input"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20] font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Lock to Guest Email</label>
                    <input
                      value={couponEmail}
                      onChange={e => setCouponEmail(e.target.value)}
                      placeholder="guest@example.com"
                      data-testid="coupon-email-input"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20]"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Lock to Guest Phone</label>
                    <input
                      value={couponPhone}
                      onChange={e => setCouponPhone(e.target.value)}
                      placeholder="9820088292"
                      data-testid="coupon-phone-input"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Notes (visible to admin only)</label>
                    <input
                      value={couponNotes}
                      onChange={e => setCouponNotes(e.target.value)}
                      placeholder="e.g. Anniversary gift for Mr Sharma"
                      data-testid="coupon-notes-input"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 focus:outline-none focus:border-[#F57C20]"
                    />
                  </div>
                </div>
                <p className="text-white/30 text-[10px] italic mb-3">Lock to at least one of email or phone so only that guest can redeem it.</p>
                <div className="flex gap-2">
                  <button
                    data-testid="create-coupon-btn"
                    disabled={couponBusy}
                    onClick={handleCreateCoupon}
                    className="bg-[#F57C20] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#e06b15] disabled:opacity-50"
                  >
                    {couponBusy ? 'Creating...' : 'Create Coupon'}
                  </button>
                  <button
                    onClick={() => { setShowCouponForm(false); setCouponMsg(null); }}
                    className="bg-white/5 text-white/70 hover:text-white border border-white/10 px-4 py-2 rounded-lg text-xs font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Coupons list */}
            {coupons.length === 0 ? (
              <p className="text-white/30 text-xs italic">No coupons created yet.</p>
            ) : (
              <div className="space-y-2">
                {coupons.map(c => (
                  <div
                    key={c.code}
                    data-testid={`coupon-row-${c.code}`}
                    className={`bg-white/5 border rounded-lg p-3 ${c.status === 'used' ? 'border-white/5 opacity-60' : 'border-white/10'}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-white text-sm font-mono font-bold tracking-wide truncate">{c.code}</span>
                        <button
                          onClick={() => handleCopyCoupon(c.code)}
                          data-testid={`coupon-copy-${c.code}`}
                          className="text-[#F57C20] hover:text-white"
                          title="Copy code"
                        >
                          {couponCopied === c.code ? <FiCheck className="w-3.5 h-3.5" /> : <FiCopy className="w-3.5 h-3.5" />}
                        </button>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex-shrink-0 ${
                          c.status === 'used'
                            ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                            : 'bg-green-500/20 text-green-300 border border-green-500/30'
                        }`}>
                          {c.status === 'used' ? '✓ Used' : '● Active'}
                        </span>
                      </div>
                      <span className="text-[#F57C20] text-sm font-bold flex-shrink-0">Rs.{c.discount_amount.toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] text-white/50">
                      {c.guest_email && <span>📧 {c.guest_email}</span>}
                      {c.guest_phone && <span>📞 {c.guest_phone}</span>}
                      {c.notes && <span className="md:col-span-2 italic">📝 {c.notes}</span>}
                      {c.status === 'used' && (
                        <>
                          <span className="text-green-400/70">Used by: {c.used_by_email || c.used_by_phone || '—'}</span>
                          <span className="text-green-400/70">On: {c.used_at ? new Date(c.used_at).toLocaleString('en-IN') : '—'}</span>
                        </>
                      )}
                    </div>
                    {c.status === 'active' && (
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        {c.guest_email && (
                          <button
                            onClick={() => handleEmailCoupon(c)}
                            disabled={couponEmailBusy[c.code]}
                            data-testid={`coupon-email-${c.code}`}
                            className="text-[10px] text-[#F57C20] hover:text-[#ffb27a] flex items-center gap-1 disabled:opacity-50"
                            title={`Email coupon to ${c.guest_email}`}
                          >
                            <FiMail className="w-3 h-3" />
                            {couponEmailBusy[c.code] ? 'Sending…' : 'Email guest'}
                            {c.email_sent_at && <span className="text-white/30 ml-1">✓ sent</span>}
                          </button>
                        )}
                        {c.guest_phone && (
                          <button
                            onClick={() => handleWhatsAppCoupon(c)}
                            data-testid={`coupon-whatsapp-${c.code}`}
                            className="text-[10px] text-[#25D366] hover:text-[#4be38c] flex items-center gap-1"
                            title={`Send on WhatsApp to ${c.guest_phone}`}
                          >
                            <FaWhatsapp className="w-3 h-3" />
                            WhatsApp guest
                          </button>
                        )}
                        <button
                          onClick={() => handleRevokeCoupon(c.code)}
                          data-testid={`coupon-revoke-${c.code}`}
                          className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 ml-auto"
                        >
                          <FiTrash2 className="w-3 h-3" /> Revoke
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recover Balance: Generate Razorpay Payment Link */}
          <div className="mt-8 bg-[#1B2432] rounded-xl p-8" data-testid="recover-balance-section">
            <div className="flex items-start gap-3 mb-5">
              <FiDollarSign className="w-5 h-5 text-[#F57C20] mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-light text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Recover Balance — Generate Payment Link</h3>
                <p className="text-white/50 text-sm">Look up a booking by ID, email, or phone. Generate a one-click Razorpay link for any outstanding amount and share with the guest via WhatsApp.</p>
              </div>
            </div>

            {/* Lookup */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mb-4">
              <input
                value={payLookup}
                onChange={(e) => setPayLookup(e.target.value)}
                placeholder="Booking ID (full or first 8 chars), email, or phone"
                className="bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F57C20]/50"
                data-testid="pay-lookup-input"
                onKeyDown={(e) => e.key === 'Enter' && handlePayLookup()}
              />
              <button
                onClick={handlePayLookup}
                data-testid="pay-lookup-btn"
                className="bg-[#F57C20] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-colors"
              >
                Look up
              </button>
            </div>

            {/* Bookings found */}
            {payBookings.length > 0 && (
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto" data-testid="pay-bookings-list">
                {payBookings.map((b) => {
                  const totalDue = (b.total_amount || 0) + (b.security_deposit_total || b.security_deposit || 0);
                  const balance = Math.max(0, totalDue - (b.amount_paid || 0));
                  return (
                    <button
                      key={b.id}
                      onClick={() => setPayTargetId(b.id)}
                      data-testid={`pay-booking-${b.id.slice(0, 8)}`}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        payTargetId === b.id ? 'border-[#F57C20] bg-[#F57C20]/10' : 'border-white/10 bg-black/20 hover:border-white/20'
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <span className="text-white text-sm font-semibold">{b.user_name || 'Guest'}</span>
                        <span className="text-white/40 text-[10px] font-mono">{b.id.slice(0, 8)}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                        <span className="text-white/50">{b.user_phone}</span>
                        <span className="text-white/50">Paid: <span className="text-white">Rs.{(b.amount_paid || 0).toLocaleString()}</span></span>
                        <span className="text-white/50">Due: <span className="text-[#F57C20] font-semibold">Rs.{balance.toLocaleString()}</span></span>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold ${
                          b.payment_status === 'completed' ? 'text-green-400' :
                          b.payment_status === 'partial' ? 'text-yellow-400' : 'text-red-400'
                        }`}>{b.payment_status || 'pending'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Amount + Note */}
            {payTargetId && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                <input
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="Amount in INR (e.g. 6500)"
                  className="bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F57C20]/50"
                  data-testid="pay-amount-input"
                />
                <input
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                  placeholder="Note (e.g. Balance correction)"
                  className="bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#F57C20]/50"
                  data-testid="pay-note-input"
                />
                <button
                  onClick={handleCreatePayLink}
                  disabled={payBusy}
                  data-testid="pay-create-link-btn"
                  className="bg-[#F57C20] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <FiLink className="w-4 h-4" /> {payBusy ? 'Creating...' : 'Generate Razorpay Link'}
                </button>
              </div>
            )}

            {/* Error */}
            {payError && (
              <div className="bg-red-950/50 border border-red-800/30 rounded-lg px-4 py-2.5 text-red-300 text-sm mb-3" data-testid="pay-error">
                {payError}
              </div>
            )}

            {/* Result */}
            {payResult && (
              <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4" data-testid="pay-result">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-300 text-sm font-semibold">Payment link created — Rs.{payResult.amount_inr.toLocaleString()}</span>
                  <a href={payResult.short_url} target="_blank" rel="noopener noreferrer" className="text-[#F57C20] text-xs hover:underline flex items-center gap-1">
                    Open <FiExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="flex items-center gap-2 bg-black/30 rounded px-3 py-2">
                  <input
                    readOnly
                    value={payResult.short_url}
                    className="flex-1 bg-transparent text-white/90 text-xs font-mono outline-none min-w-0"
                    data-testid="pay-result-url"
                  />
                  <button
                    onClick={handleCopyPayLink}
                    className="text-[#F57C20] hover:text-white transition-colors flex-shrink-0"
                    data-testid="pay-result-copy"
                    title="Copy link"
                  >
                    {payCopied ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-white/40 text-[11px] mt-2">
                  Share this link via WhatsApp. Once the guest pays, come back and record the payment against the booking so reports stay accurate.
                </p>
              </div>
            )}

            {/* Send Balance Reminder Email (manual trigger) */}
            {payTargetId && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-black/20 border border-white/10 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold">Email guest a balance reminder now</p>
                  <p className="text-white/40 text-[11px]">Sends a styled email with a &ldquo;Pay Balance Now&rdquo; button linking to their dashboard.</p>
                </div>
                <button
                  onClick={handleSendReminder}
                  disabled={reminderBusy}
                  data-testid="send-reminder-btn"
                  className="bg-blue-500/90 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {reminderBusy ? 'Sending...' : 'Send Reminder Email'}
                </button>
                {reminderMsg && (
                  <div className={`w-full rounded-lg px-3 py-2 text-xs ${
                    reminderMsg.type === 'success' ? 'bg-green-900/20 border border-green-700/30 text-green-300'
                                                    : 'bg-red-950/50 border border-red-800/30 text-red-300'
                  }`}>{reminderMsg.text}</div>
                )}
              </div>
            )}

            {/* Fix Booking Totals (for legacy/incorrect bookings) */}
            {payTargetId && (
              <details className="mt-5 bg-black/20 border border-white/10 rounded-lg p-4">
                <summary className="cursor-pointer text-white/80 text-sm font-semibold flex items-center gap-2">
                  <FiAlertTriangle className="w-4 h-4 text-yellow-400" />
                  Fix booking totals (advanced — for bookings created with old/incorrect pricing)
                </summary>
                <p className="text-white/40 text-xs mt-2 mb-3">
                  Use this only if you need to correct the stored Grand Total or Amount Paid on this booking.
                  The Outstanding amount the guest sees on their dashboard will update automatically.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="number"
                    value={fixGrandTotal}
                    onChange={(e) => setFixGrandTotal(e.target.value)}
                    placeholder="Correct Grand Total (e.g. 30997)"
                    className="bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-yellow-400/50"
                    data-testid="fix-grand-total-input"
                  />
                  <input
                    type="number"
                    value={fixAmountPaid}
                    onChange={(e) => setFixAmountPaid(e.target.value)}
                    placeholder="Amount Paid so far (e.g. 12249)"
                    className="bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-yellow-400/50"
                    data-testid="fix-amount-paid-input"
                  />
                  <button
                    onClick={handleFixTotals}
                    disabled={fixBusy}
                    data-testid="fix-totals-btn"
                    className="bg-yellow-500 text-[#1A1A2E] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50"
                  >
                    {fixBusy ? 'Updating...' : 'Update Totals'}
                  </button>
                </div>
                {fixError && (
                  <div className="mt-3 bg-red-950/50 border border-red-800/30 rounded-lg px-4 py-2 text-red-300 text-xs">{fixError}</div>
                )}
                {fixResult && (
                  <div className="mt-3 bg-green-900/20 border border-green-700/30 rounded-lg px-4 py-2 text-green-300 text-xs">
                    Updated · Grand Total: Rs.{fixResult.grand_total.toLocaleString()} · Paid: Rs.{fixResult.amount_paid.toLocaleString()} · Outstanding: Rs.{fixResult.outstanding.toLocaleString()}
                  </div>
                )}
              </details>
            )}
          </div>

          {/* ===== B2B BOOKING AGENTS (register + manage + commissions) ===== */}
          <B2BAgentsPanel />
          <QuoteModal open={showQuote} onClose={() => setShowQuote(false)} mode="admin" title="Generate Quotation" />

          {/* ===== GUEST FEEDBACK (post-stay reviews collected via /feedback) ===== */}
          <div className="mt-8 bg-[#1B2432] rounded-xl p-8" data-testid="guest-feedback-section">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div className="flex items-start gap-3">
                <FiStar className="w-5 h-5 text-[#F57C20] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-light text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Guest Feedback</h3>
                  <p className="text-white/60 text-sm">Post-stay reviews submitted on <code className="text-white/90">/feedback</code> &middot; also emailed to royalgardenlonavala@gmail.com.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid="feedback-refresh-btn" onClick={loadFeedback} disabled={feedbackBusy}
                  className="bg-white/10 hover:bg-white/15 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                  <FiRefreshCw className={`w-3.5 h-3.5 ${feedbackBusy ? 'animate-spin' : ''}`} /> Refresh
                </button>
                <button data-testid="feedback-export-btn" onClick={exportFeedbackCSV} disabled={!feedback.length}
                  className="bg-[#F57C20] hover:bg-[#E06A10] text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                  <FiDownload className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
            </div>

            {/* Summary stats */}
            {feedback.length > 0 && (() => {
              const total = feedback.length;
              const avgRating = (feedback.reduce((s, f) => s + (f.rating || 0), 0) / total).toFixed(1);
              const nps = Math.round((feedback.filter(f => f.recommend).length / total) * 100);
              return (
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-3">
                    <p className="text-white/50 text-[10px] uppercase tracking-wider">Total</p>
                    <p className="text-white text-2xl font-light">{total}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-3">
                    <p className="text-white/50 text-[10px] uppercase tracking-wider">Avg Rating</p>
                    <p className="text-[#F57C20] text-2xl font-light">{avgRating} <span className="text-sm text-white/60">★</span></p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-3">
                    <p className="text-white/50 text-[10px] uppercase tracking-wider">Would Recommend</p>
                    <p className="text-emerald-400 text-2xl font-light">{nps}%</p>
                  </div>
                </div>
              );
            })()}

            {feedbackBusy && !feedback.length ? (
              <p className="text-white/50 text-sm">Loading feedback…</p>
            ) : feedback.length === 0 ? (
              <p className="text-white/50 text-sm">No feedback yet. Guests who check out today will receive an email request automatically.</p>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="feedback-list">
                {feedback.map(f => (
                  <div key={f.id} data-testid={`feedback-item-${f.id}`} className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-white text-sm font-semibold">{f.guest_name} <span className="text-white/40 font-normal">· {f.villa_name}</span></p>
                        <p className="text-white/50 text-xs">{f.guest_email} &middot; {f.guest_phone} &middot; Checkout {f.check_out}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[#F57C20] text-sm" title={`${f.rating}/5`}>{'★'.repeat(f.rating)}<span className="text-white/20">{'★'.repeat(5 - f.rating)}</span></span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${f.recommend ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                          {f.recommend ? 'Recommends' : 'Would not'}
                        </span>
                        <button onClick={() => deleteFeedback(f.id)} title="Delete" className="text-white/30 hover:text-red-400 p-1">
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {f.loved && <p className="text-emerald-300 text-xs mt-2"><strong>Loved:</strong> {f.loved}</p>}
                    {f.improve && <p className="text-amber-300 text-xs mt-1"><strong>Improve:</strong> {f.improve}</p>}
                    <p className="text-white/30 text-[10px] mt-2">{new Date(f.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Emergency: Purge Blocked Dates */}
          <div className="mt-8 bg-red-950 rounded-xl p-8 border border-red-800/30" data-testid="purge-section">
            <div className="flex items-start gap-3 mb-4">
              <FiAlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-light text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Emergency: Clear Blocked Dates</h3>
                <p className="text-red-300/60 text-sm">Remove all ghost/stale blocked dates. Airbnb blocks will re-sync automatically within 1 hour.</p>
              </div>
            </div>
            {purgeResult && (
              <div className="bg-green-900/30 border border-green-700/30 rounded-lg px-4 py-3 mb-4 text-green-300 text-sm">
                {purgeResult}
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="purge-all-btn"
                disabled={purging}
                onClick={async () => {
                  if (!window.confirm('This will remove ALL blocked dates across ALL villas. Airbnb bookings will re-sync within 1 hour. Proceed?')) return;
                  setPurging(true);
                  try {
                    const res = await api.delete('/admin/purge-all-blocks');
                    setPurgeResult(`Cleared ${res.data.deleted} blocked entries across all villas.`);
                  } catch (err) {
                    setPurgeResult('Failed: ' + (err.response?.data?.detail || 'Unknown error'));
                  }
                  setPurging(false);
                }}
                className="bg-red-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <FiTrash2 className="w-4 h-4" /> {purging ? 'Clearing...' : 'Clear ALL Villas'}
              </button>
              {['3bhk-garden-escape-lonavala', '4bhk-garden-retreat-lonavala', '5bhk-garden-delight-lonavala', '12bhk-celebration-villa-lonavala'].map(vid => (
                <button
                  key={vid}
                  disabled={purging}
                  onClick={async () => {
                    const label = vid.split('-')[0].toUpperCase();
                    if (!window.confirm(`Clear all blocked dates for ${label}?`)) return;
                    setPurging(true);
                    try {
                      const res = await api.delete(`/admin/purge-blocks/${vid}`);
                      setPurgeResult(`Cleared ${res.data.deleted} entries for ${label}.`);
                    } catch (err) {
                      setPurgeResult('Failed: ' + (err.response?.data?.detail || 'Unknown error'));
                    }
                    setPurging(false);
                  }}
                  className="bg-red-900/50 text-red-300 border border-red-700/30 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-red-800/50 transition-colors disabled:opacity-50"
                >
                  {vid.split('-')[0].toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
